import { lazy } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { Routes, SyncHook } from '../route-types';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { ReactNode } from 'react';
import { Permission } from '@act/shared/authorization';
import { DOCKET_PREFIX_PATH } from './docket.routes';
import { needsContentRoutes } from './needs-admin-routes';

// Lazy load all admin dashboard components
const CustomerView = lazy(() =>
  import('react-components/admin-dashboard/components/Customer/CustomerView').then(
    module => ({
      default: module.CustomerView,
    })
  )
);
const UserAdminTable = lazy(() =>
  import(
    'react-components/admin-dashboard/components/user-creation/UserAdminTable'
  ).then(module => ({
    default: module.UserAdminTable,
  }))
);
const CreateEditUserPage = lazy(() =>
  import(
    'react-components/admin-dashboard/components/user-creation/CreateEditUser/CreateEditUser'
  ).then(module => ({
    default: module.CreateEditUserPage,
  }))
);
const TeamsTable = lazy(() =>
  import('react-components/admin-dashboard/components/teams/TeamsTable').then(
    module => ({
      default: module.TeamsTable,
    })
  )
);
const SurveyTable = lazy(() =>
  import(
    'react-components/admin-dashboard/components/Survey/SurveyTable'
  ).then(module => ({
    default: module.SurveyTable,
  }))
);
const PhoneNumbersTable = lazy(() =>
  import(
    'react-components/admin-dashboard/components/Phone-numbers/PhoneNumbersTable'
  ).then(module => ({
    default: module.PhoneNumbersTable,
  }))
);
const MessageContent = lazy(() =>
  import(
    'react-components/admin-dashboard/components/MessageContent/MessageContent'
  ).then(module => ({
    default: module.MessageContent,
  }))
);
const MessageTemplatesTable = lazy(() =>
  import(
    'react-components/admin-dashboard/components/MessageContent/MessageTemplate/MessageTemplatesTable'
  ).then(module => ({
    default: module.MessageTemplatesTable,
  }))
);
const MessageTemplateCategoriesTable = lazy(() =>
  import(
    'react-components/admin-dashboard/components/MessageContent/MessageTemplateCategory/MessageTemplateCategoriesTable'
  ).then(module => ({
    default: module.MessageTemplateCategoriesTable,
  }))
);
const MultipanelContainer = lazy(() =>
  import(
    'react-components/admin-dashboard/components/Survey/SurveyCreatorTool/components/MultipanelContainer'
  ).then(module => ({
    default: module.default,
  }))
);
const CustomizationThemeView = lazy(() =>
  import(
    'react-components/admin-dashboard/components/CustomizationTheme/CustomizationThemeView'
  ).then(module => ({
    default: module.CustomizationThemeView,
  }))
);
export const ADMIN_DASHBOARD_PREFIX_PATH = '/admin-dashboard';

const useSyncHook: SyncHook = () => {};

interface Props {
  children: ReactNode;
}

export const AdminDashboardRoutes = ({ children }: Props) => {
  // if its an admin take them to the page, otherwise reroute to docket main page
  const isAdmin = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );
  {
    return isAdmin ? <>{children}</> : <Navigate to={DOCKET_PREFIX_PATH} />;
  }
};

const routesList: Routes = [
  {
    path: '/admin-dashboard',
    element: (
      <Navigate to={`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation`} replace />
    ),
  },
  {
    path: '/admin-dashboard/customer',
    element: <CustomerView />,
  },
  {
    path: '/admin-dashboard/customer/:id',
    element: <CustomerView />,
  },
  {
    path: '/admin-dashboard/user-creation',
    element: <UserAdminTable />,
  },
  {
    path: '/admin-dashboard/user-creation/edit/:userId',
    element: <CreateEditUserPage />,
  },
  {
    path: '/admin-dashboard/user-creation/create',
    element: <CreateEditUserPage />,
  },
  {
    path: '/admin-dashboard/teams',
    element: <TeamsTable />,
  },
  {
    path: '/admin-dashboard/phone-numbers',
    element: <PhoneNumbersTable />,
  },
  {
    path: '/admin-dashboard/surveys',
    element: <SurveyTable />,
  },
  {
    path: '/admin-dashboard/survey-creator',
    element: <MultipanelContainer />,
  },
  {
    path: '/admin-dashboard/message-templates',
    element: (
      <MessageContent>
        <MessageTemplateCategoriesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/message-templates/categories/:id',
    element: (
      <MessageContent>
        <MessageTemplatesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/message-templates/categories/:id/edit',
    element: (
      <MessageContent>
        <MessageTemplateCategoriesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/message-templates/categories/:categoryId/templates/:templateId/edit',
    element: (
      <MessageContent>
        <MessageTemplatesTable />
      </MessageContent>
    ),
  },
  {
    path: '/admin-dashboard/customization-themes',
    element: <CustomizationThemeView />,
  },
  ...needsContentRoutes,
];

const routes: Routes = routesList.map(({ path, element }) => ({
  path,
  element: <AdminDashboardRoutes>{element}</AdminDashboardRoutes>,
}));

const useNavigationHooks = () => {
  // Only call useNavigate when we're actually inside a Router context
  // This prevents the error when the hook is called during initial render
  let navigate: ReturnType<typeof useNavigate> | null = null;

  try {
    navigate = useNavigate();
  } catch (error) {
    // If we're not in a Router context, navigate will be null
    // This is expected during initial render
  }

  return {
    toAdminDashboard: () => {
      if (navigate) navigate(ADMIN_DASHBOARD_PREFIX_PATH);
    },
    toCustomerView: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customer`);
    },
    toNewCustomerView: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customer/new`);
    },
    toEditCustomerView: (id: number) => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customer/${id}`);
    },
    toUserCreation: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation`);
    },
    toUserCreationEdit: (userId: string) => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation/edit/${userId}`);
    },
    toUserCreationCreate: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/user-creation/create`);
    },
    toTeamsManagement: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/teams`);
    },
    toSurveys: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/surveys`);
    },
    toSurveyCreator: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/survey-creator`);
    },
    toPhoneNumberManagement: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/phone-numbers`);
    },
    toAllMessageCategories: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates`, {});
    },
    toMessageCategory: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${id}`
        );
    },

    toMessageTemplateEdit: (categoryId: number, templateId: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${categoryId}/templates/${templateId}/edit`
        );
    },
    toMessageCategoryEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${id}/edit`
        );
    },
    toMessageTemplateCreate: (categoryId: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/${categoryId}/templates/new`
        );
    },
    toMessageTemplateCategoryCreate: () => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/message-templates/categories/new`
        );
    },
    toNeedsContent: () => {
      if (navigate) navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content`);
    },
    toNeedsCategory: (key: string) => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/${key}`);
    },
    toNeedsCategoryEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/categories/${id}/edit`
        );
    },
    toNeedsIssue: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/issues`);
    },
    toNeedsIssueEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/issues/${id}/edit`
        );
    },
    toNeedsActivityEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/activities/${id}/edit`
        );
    },
    toNeedsDomainEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/domains/${id}/edit`
        );
    },
    toNeedsZcodeEdit: (id: number) => {
      if (navigate)
        navigate(
          `${ADMIN_DASHBOARD_PREFIX_PATH}/needs-content/zcodes/${id}/edit`
        );
    },
    toCustomizationThemes: () => {
      if (navigate)
        navigate(`${ADMIN_DASHBOARD_PREFIX_PATH}/customization-themes`);
    },
  };
};

export const adminDashboardRouteConfig = {
  routes,
  useSyncHook,
  hideNavigationHeader: true,
  useNavigationHooks,
  publicRoute: false,
};
